
import { useState } from 'react';
import { useBabies } from '../../hooks/useBabies';
import type { Baby } from '../../lib/supabase';

interface BabySelectorProps {
  onBabyChange?: (baby: Baby | null) => void;
}

export default function BabySelector({ onBabyChange }: BabySelectorProps) {
  const { babies, activeBaby, setActiveBaby, deleteBaby } = useBabies();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);

  const handleBabySelect = (baby: Baby) => {
    setActiveBaby(baby);
    onBabyChange?.(baby);
    setShowDropdown(false);
  };

  const handleDeleteBaby = async (babyId: string) => {
    console.log('Iniciando eliminación desde modal, ID:', babyId);
    const result = await deleteBaby(babyId);
    
    if (!result.error) {
      console.log('Eliminación exitosa, actualizando interfaz');
      // Notificar cambio si no quedan bebés
      if (babies.length === 1) {
        onBabyChange?.(null);
      }
    }
    
    setShowDeleteModal(null);
    setShowDropdown(false);
  };

  if (babies.length === 0) {
    return (
      <div className="flex items-center justify-center p-4 bg-gray-50 rounded-xl">
        <div className="text-center">
          <i className="ri-baby-line text-3xl text-gray-400 mb-2"></i>
          <p className="text-gray-500 text-sm">No hay bebés registrados</p>
        </div>
      </div>
    );
  }

  if (babies.length === 1) {
    return (
      <div className="flex items-center justify-between px-4 py-2 bg-white/80 backdrop-blur-sm rounded-xl">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center">
            {activeBaby?.photo_url ? (
              <img src={activeBaby.photo_url} alt={activeBaby.name} className="w-full h-full object-cover" />
            ) : (
              <i className="ri-baby-line text-white text-sm"></i>
            )}
          </div>
          <div>
            <p className="font-medium text-gray-800">{activeBaby?.name}</p>
            <p className="text-xs text-gray-500">
              {activeBaby?.birth_date && new Date(activeBaby.birth_date).toLocaleDateString()}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            console.log('Click en botón eliminar (bebé único), ID:', activeBaby?.id);
            setShowDeleteModal(activeBaby?.id || null);
          }}
          className="p-1 hover:bg-red-50 rounded-full transition-colors"
          title="Eliminar bebé"
        >
          <i className="ri-delete-bin-line text-red-500 text-sm"></i>
        </button>

        {/* Modal de confirmación para bebé único */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
              <div className="text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-delete-bin-line text-red-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">¿Eliminar bebé?</h3>
                <p className="text-gray-600 text-sm mb-6">
                  Esta acción eliminará todos los registros asociados y no se puede deshacer.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      console.log('Cancelando eliminación');
                      setShowDeleteModal(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      console.log('Confirmando eliminación del bebé único');
                      handleDeleteBaby(showDeleteModal);
                    }}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-red-700 transition-colors"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center space-x-3 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-xl hover:bg-white/90 transition-colors w-full"
        >
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center">
            {activeBaby?.photo_url ? (
              <img src={activeBaby.photo_url} alt={activeBaby.name} className="w-full h-full object-cover" />
            ) : (
              <i className="ri-baby-line text-white text-sm"></i>
            )}
          </div>
          <div className="flex-1 text-left">
            <p className="font-medium text-gray-800">{activeBaby?.name}</p>
            <p className="text-xs text-gray-500">
              {activeBaby?.birth_date && new Date(activeBaby.birth_date).toLocaleDateString()}
            </p>
          </div>
          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`}></i>
        </button>

        {showDropdown && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 z-50">
            {babies.map((baby) => (
              <div
                key={baby.id}
                className={`flex items-center justify-between px-4 py-3 hover:bg-gray-50 first:rounded-t-xl last:rounded-b-xl transition-colors ${
                  baby.id === activeBaby?.id ? 'bg-blue-50' : ''
                }`}
              >
                <button
                  onClick={() => handleBabySelect(baby)}
                  className="flex items-center space-x-3 flex-1"
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center">
                    {baby.photo_url ? (
                      <img src={baby.photo_url} alt={baby.name} className="w-full h-full object-cover" />
                    ) : (
                      <i className="ri-baby-line text-white text-sm"></i>
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-800">{baby.name}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(baby.birth_date).toLocaleDateString()}
                    </p>
                  </div>
                  {baby.id === activeBaby?.id && (
                    <i className="ri-check-line text-blue-600"></i>
                  )}
                </button>
                <button
                  onClick={() => {
                    console.log('Click en botón eliminar (dropdown), ID:', baby.id);
                    setShowDeleteModal(baby.id);
                  }}
                  className="p-1 hover:bg-red-50 rounded-full transition-colors ml-2"
                  title="Eliminar bebé"
                >
                  <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de confirmación de eliminación para dropdown */}
      {showDeleteModal && babies.length > 1 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-delete-bin-line text-red-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">¿Eliminar bebé?</h3>
              <p className="text-gray-600 text-sm mb-6">
                Esta acción eliminará todos los registros asociados y no se puede deshacer.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDeleteBaby(showDeleteModal)}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-red-700 transition-colors"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
